/**
 * Email template registry and variable definitions
 * This is the source of truth for all available email templates and their variables
 */

import {
  DEFAULT_TASK_REMINDER_SUBJECT,
  DEFAULT_TASK_REMINDER_HTML,
  DEFAULT_TASK_REMINDER_TEXT,
  DEFAULT_PROFILE_COMPLETION_SUBJECT,
  DEFAULT_PROFILE_COMPLETION_HTML,
  DEFAULT_PROFILE_COMPLETION_TEXT,
  DEFAULT_HAPPENING_COMPLETION_SUBJECT,
  DEFAULT_HAPPENING_COMPLETION_HTML,
  DEFAULT_HAPPENING_COMPLETION_TEXT,
  DEFAULT_HAPPENING_STUB_LINK_SUBJECT,
  DEFAULT_HAPPENING_STUB_LINK_HTML,
  DEFAULT_HAPPENING_STUB_LINK_TEXT,
  DEFAULT_HAPPENING_REMINDER_SUBJECT,
  DEFAULT_HAPPENING_REMINDER_HTML,
  DEFAULT_HAPPENING_REMINDER_TEXT,
  DEFAULT_COMPLETION_NOTIFICATION_SUBJECT,
  DEFAULT_COMPLETION_NOTIFICATION_HTML,
  DEFAULT_COMPLETION_NOTIFICATION_TEXT,
  DEFAULT_ARTIST_AND_SHOW_INVITE_SUBJECT,
  DEFAULT_ARTIST_AND_SHOW_INVITE_HTML,
  DEFAULT_ARTIST_AND_SHOW_INVITE_TEXT,
  DEFAULT_RESIDENCY_CREATION_SUBJECT,
  DEFAULT_RESIDENCY_CREATION_HTML,
  DEFAULT_RESIDENCY_CREATION_TEXT,
  DEFAULT_POST_SHOW_SUBJECT,
  DEFAULT_POST_SHOW_HTML,
  DEFAULT_POST_SHOW_TEXT,
  DEFAULT_TECHNICAL_UPDATE_NOTIFICATION_SUBJECT,
  DEFAULT_TECHNICAL_UPDATE_NOTIFICATION_HTML,
  DEFAULT_TECHNICAL_UPDATE_NOTIFICATION_TEXT,
  DEFAULT_ARCHIVE_NOTIFICATION_SUBJECT,
  DEFAULT_ARCHIVE_NOTIFICATION_HTML,
  DEFAULT_ARCHIVE_NOTIFICATION_TEXT,
} from './email-templates-defaults.js';

export type EmailType =
  | 'taskReminder'
  | 'profileCompletion'
  | 'happeningCompletion'
  | 'happeningStubLink'
  | 'happeningReminder'
  | 'completionNotification'
  | 'artistAndShowInvite'
  | 'residencyCreation'
  | 'postShow'
  | 'technicalUpdateNotification'
  | 'archiveNotification';

export type VariableDataType = 'string' | 'date' | 'number' | 'url' | 'object' | 'html';

export interface TemplateVariable {
  name: string;              // Variable name: e.g., "toName"
  key: string;               // Mustache key: "{{toName}}"
  description: string;       // Human-readable description
  example: string;           // Example value for preview
  required: boolean;         // Is this variable required?
  dataType: VariableDataType; // Type of data
  category?: string;         // Grouping category (recipient, task, org, etc.)
}

export interface EmailTemplateSchema {
  type: EmailType;
  name: string;              // Display name: "Task Reminder"
  description: string;       // When this email is sent
  variables: TemplateVariable[];
  defaultSubject: string;    // Default subject line
  defaultHtml: string;       // Default HTML template
  defaultText: string;       // Default text template
  sampleData: Record<string, any>; // Sample data for preview
}

/**
 * Email Template Registry - Source of truth for all email templates
 */
export const EMAIL_TEMPLATE_REGISTRY: Record<EmailType, EmailTemplateSchema> = {
  taskReminder: {
    type: 'taskReminder',
    name: 'Task Reminder',
    description: 'Sent to remind users about upcoming tasks before their due date',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Recipient email address',
        example: 'alice@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Recipient name',
        example: 'Alice Johnson',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'taskTitle',
        key: '{{taskTitle}}',
        description: 'Title of the task',
        example: 'Prepare sound check',
        required: true,
        dataType: 'string',
        category: 'task',
      },
      {
        name: 'taskDescription',
        key: '{{taskDescription}}',
        description: 'Detailed description of the task (optional)',
        example: 'Set up microphones and test all audio equipment before the show',
        required: false,
        dataType: 'string',
        category: 'task',
      },
      {
        name: 'taskDueDate',
        key: '{{taskDueDate}}',
        description: 'Formatted due date and time',
        example: 'Monday, January 15, 2026 at 2:00 PM',
        required: true,
        dataType: 'date',
        category: 'task',
      },
      {
        name: 'taskType',
        key: '{{taskType}}',
        description: 'Type of task (preparation, follow_up, production, etc.)',
        example: 'preparation',
        required: true,
        dataType: 'string',
        category: 'task',
      },
      {
        name: 'taskPriority',
        key: '{{taskPriority}}',
        description: 'Priority level (low, medium, high, urgent)',
        example: 'high',
        required: true,
        dataType: 'string',
        category: 'task',
      },
      {
        name: 'taskPriorityColor',
        key: '{{taskPriorityColor}}',
        description: 'Color code for priority level',
        example: '#ea580c',
        required: false,
        dataType: 'string',
        category: 'task',
      },
      {
        name: 'taskLink',
        key: '{{taskLink}}',
        description: 'Link to view the task in the admin interface',
        example: 'https://admin.littlebooker.com/tasks?openEntityId=123',
        required: true,
        dataType: 'url',
        category: 'task',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color (from branding settings)',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color (from branding settings)',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo (from branding settings)',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
      {
        name: 'relatedHappening.title',
        key: '{{relatedHappening.title}}',
        description: 'Title of related happening/show (if task is linked to a show)',
        example: 'Live Jazz Night',
        required: false,
        dataType: 'string',
        category: 'related',
      },
      {
        name: 'relatedHappening.startTime',
        key: '{{relatedHappening.startTime}}',
        description: 'Start time of related happening',
        example: 'Saturday, January 20, 2026 at 8:00 PM',
        required: false,
        dataType: 'date',
        category: 'related',
      },
      {
        name: 'relatedHappening.type',
        key: '{{relatedHappening.type}}',
        description: 'Type of related happening',
        example: 'live-show',
        required: false,
        dataType: 'string',
        category: 'related',
      },
      {
        name: 'relatedHappening.place.name',
        key: '{{relatedHappening.place.name}}',
        description: 'Name of venue for related happening',
        example: 'Studio A',
        required: false,
        dataType: 'string',
        category: 'related',
      },
      {
        name: 'relatedHappening.place.address',
        key: '{{relatedHappening.place.address}}',
        description: 'Address of venue',
        example: '123 Music Street, Amsterdam',
        required: false,
        dataType: 'string',
        category: 'related',
      },
    ],
    defaultSubject: DEFAULT_TASK_REMINDER_SUBJECT,
    defaultHtml: DEFAULT_TASK_REMINDER_HTML,
    defaultText: DEFAULT_TASK_REMINDER_TEXT,
    sampleData: {
      toEmail: 'alice@example.com',
      toName: 'Alice',
      taskTitle: 'Prepare sound check',
      taskDescription: 'Set up microphones and test all audio equipment before the show',
      taskDueDate: 'Monday, January 15, 2026 at 2:00 PM',
      taskType: 'preparation',
      taskPriority: 'high',
      taskPriorityColor: '#ea580c',
      taskDescriptionHtml: '<p style="margin: 10px 0; color: #6b7280; font-size: 14px;">Set up microphones and test all audio equipment before the show</p>',
      taskLink: 'https://admin.littlebooker.com/tasks?openEntityId=123',
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
      happeningSectionHtml: `
        <div style="background: #f0f9ff; border-left: 4px solid #764ba2; padding: 15px; margin: 20px 0; border-radius: 4px;">
          <h3 style="margin: 0 0 10px 0; font-size: 16px; color: #111827;">Related Happening</h3>
          <p style="margin: 0 0 8px 0; font-size: 14px; color: #6b7280;">
            <strong style="color: #111827;">Live Jazz Night</strong>
            <span style="margin-left: 10px; padding: 2px 8px; background: #667eea; color: white; border-radius: 3px; font-size: 12px; text-transform: capitalize;">live-show</span>
          </p>
          <p style="margin: 0; font-size: 13px; color: #6b7280;">
            <strong>Starts:</strong> Saturday, January 20, 2026 at 8:00 PM
          </p>
          <p style="margin: 8px 0 0 0; font-size: 13px; color: #6b7280;">
            <strong>Location:</strong> Studio A — 123 Music Street, Amsterdam
          </p>
        </div>
      `,
      happeningSectionText: `
Related Happening:
Live Jazz Night (live-show)
Starts: Saturday, January 20, 2026 at 8:00 PM
Location: Studio A
123 Music Street, Amsterdam
      `.trim(),
      relatedHappening: {
        title: 'Live Jazz Night',
        startTime: 'Saturday, January 20, 2026 at 8:00 PM',
        type: 'live-show',
        place: {
          name: 'Studio A',
          address: '123 Music Street, Amsterdam',
        },
      },
    },
  },

  profileCompletion: {
    type: 'profileCompletion',
    name: 'Profile Completion Reminder',
    description: 'Sent to artists/people who need to complete their profile information',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Recipient email address',
        example: 'bob@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Recipient name',
        example: 'Bob Smith',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'personName',
        key: '{{personName}}',
        description: 'Name of the person whose profile needs completion',
        example: 'Bob Smith',
        required: true,
        dataType: 'string',
        category: 'profile',
      },
      {
        name: 'missingFieldsList',
        key: '{{missingFieldsList}}',
        description: 'HTML formatted list of missing fields',
        example: '• Email<br>• Phone<br>• Bio',
        required: true,
        dataType: 'html',
        category: 'profile',
      },
      {
        name: 'missingFieldsText',
        key: '{{missingFieldsText}}',
        description: 'Plain text list of missing fields',
        example: '• Email\n• Phone\n• Bio',
        required: true,
        dataType: 'string',
        category: 'profile',
      },
      {
        name: 'inviteLink',
        key: '{{inviteLink}}',
        description: 'Link to complete the profile',
        example: 'https://portal.littlebooker.com/profile/complete/abc123',
        required: true,
        dataType: 'url',
        category: 'profile',
      },
      {
        name: 'completionDeadline',
        key: '{{completionDeadline}}',
        description: 'Formatted deadline for profile completion',
        example: 'Friday, January 19, 2026',
        required: true,
        dataType: 'date',
        category: 'profile',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_PROFILE_COMPLETION_SUBJECT,
    defaultHtml: DEFAULT_PROFILE_COMPLETION_HTML,
    defaultText: DEFAULT_PROFILE_COMPLETION_TEXT,
    sampleData: {
      toEmail: 'bob@example.com',
      toName: 'Bob',
      personName: 'Bob Smith',
      missingFieldsList: '• Email<br>• Phone<br>• Bio<br>• Picture',
      missingFieldsText: '• Email\n• Phone\n• Bio\n• Picture',
      inviteLink: 'https://portal.littlebooker.com/profile/complete/abc123',
      completionDeadline: 'Friday, January 19, 2026',
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
    },
  },

  happeningCompletion: {
    type: 'happeningCompletion',
    name: 'Happening Completion Reminder',
    description: 'Sent when a happening/show has missing details that need to be completed',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Recipient email address',
        example: 'carol@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Recipient name',
        example: 'Carol Davis',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'happeningTitle',
        key: '{{happeningTitle}}',
        description: 'Title of the happening/show',
        example: 'Monday Night Jazz Session',
        required: true,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'happeningStartTime',
        key: '{{happeningStartTime}}',
        description: 'Formatted start time of the happening',
        example: 'Monday, January 22, 2026 at 7:00 PM',
        required: true,
        dataType: 'date',
        category: 'happening',
      },
      {
        name: 'missingFieldsList',
        key: '{{missingFieldsList}}',
        description: 'HTML formatted list of missing fields',
        example: '• Description<br>• Artists<br>• Duration',
        required: true,
        dataType: 'html',
        category: 'happening',
      },
      {
        name: 'missingFieldsText',
        key: '{{missingFieldsText}}',
        description: 'Plain text list of missing fields',
        example: '• Description\n• Artists\n• Duration',
        required: true,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'stubLink',
        key: '{{stubLink}}',
        description: 'Link to complete the happening details',
        example: 'https://portal.littlebooker.com/show/complete/xyz789',
        required: true,
        dataType: 'url',
        category: 'happening',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_HAPPENING_COMPLETION_SUBJECT,
    defaultHtml: DEFAULT_HAPPENING_COMPLETION_HTML,
    defaultText: DEFAULT_HAPPENING_COMPLETION_TEXT,
    sampleData: {
      toEmail: 'carol@example.com',
      toName: 'Carol',
      happeningTitle: 'Monday Night Jazz Session',
      happeningStartTime: 'Monday, January 22, 2026 at 7:00 PM',
      missingFieldsList: '• Description<br>• Artists<br>• Duration',
      missingFieldsText: '• Description\n• Artists\n• Duration',
      stubLink: 'https://portal.littlebooker.com/show/complete/xyz789',
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
    },
  },

  happeningStubLink: {
    type: 'happeningStubLink',
    name: 'Happening Stub Link',
    description: 'Sent immediately after creating a happening stub with a link to complete details',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Recipient email address',
        example: 'dave@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Recipient name',
        example: 'Dave Wilson',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'happeningTitle',
        key: '{{happeningTitle}}',
        description: 'Title of the happening/show',
        example: 'Thursday Evening Show',
        required: true,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'stubLink',
        key: '{{stubLink}}',
        description: 'Link to complete the happening details',
        example: 'https://portal.littlebooker.com/show/complete/def456',
        required: true,
        dataType: 'url',
        category: 'happening',
      },
      {
        name: 'completionDeadline',
        key: '{{completionDeadline}}',
        description: 'Formatted deadline for completing details',
        example: 'Wednesday, January 24, 2026',
        required: true,
        dataType: 'date',
        category: 'happening',
      },
      {
        name: 'happeningStartTime',
        key: '{{happeningStartTime}}',
        description: 'Formatted start date and time of the happening',
        example: 'Friday, January 26 at 10:00 PM',
        required: false,
        dataType: 'date',
        category: 'happening',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_HAPPENING_STUB_LINK_SUBJECT,
    defaultHtml: DEFAULT_HAPPENING_STUB_LINK_HTML,
    defaultText: DEFAULT_HAPPENING_STUB_LINK_TEXT,
    sampleData: {
      toEmail: 'dave@example.com',
      toName: 'Dave',
      happeningTitle: 'Thursday Evening Show',
      stubLink: 'https://portal.littlebooker.com/show/complete/def456',
      completionDeadline: 'Wednesday, January 24, 2026',
      happeningStartTime: 'Friday, January 26 at 10:00 PM',
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
    },
  },

  happeningReminder: {
    type: 'happeningReminder',
    name: 'Happening Reminder',
    description: 'Sent to artists/performers before an upcoming show or happening',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Recipient email address',
        example: 'eve@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Recipient name',
        example: 'Eve Martinez',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'happeningTitle',
        key: '{{happeningTitle}}',
        description: 'Title of the happening/show',
        example: 'Weekend Spotlight Session',
        required: true,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'happeningStartTime',
        key: '{{happeningStartTime}}',
        description: 'Formatted start time',
        example: 'Saturday, January 27, 2026 at 6:00 PM',
        required: true,
        dataType: 'date',
        category: 'happening',
      },
      {
        name: 'happeningPlace.name',
        key: '{{happeningPlace.name}}',
        description: 'Name of the venue',
        example: 'Main Studio',
        required: false,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'happeningPlace.address',
        key: '{{happeningPlace.address}}',
        description: 'Address of the venue',
        example: '456 Performance Ave, Rotterdam',
        required: false,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'arrivalInfo',
        key: '{{arrivalInfo}}',
        description: 'Optional arrival time or special instructions',
        example: 'Please arrive at 5:30 PM for sound check',
        required: false,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'locationSectionHtml',
        key: '{{locationSectionHtml}}',
        description: 'HTML for location section (auto-generated if place exists)',
        example: '<div>Location details...</div>',
        required: false,
        dataType: 'html',
        category: 'happening',
      },
      {
        name: 'locationSectionText',
        key: '{{locationSectionText}}',
        description: 'Plain text for location section',
        example: 'Location: Main Studio\n456 Performance Ave, Rotterdam',
        required: false,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'arrivalSectionHtml',
        key: '{{arrivalSectionHtml}}',
        description: 'HTML for arrival instructions (if provided)',
        example: '<div>Arrival details...</div>',
        required: false,
        dataType: 'html',
        category: 'happening',
      },
      {
        name: 'arrivalSectionText',
        key: '{{arrivalSectionText}}',
        description: 'Plain text for arrival instructions',
        example: 'Arrival: Please arrive at 5:30 PM for sound check',
        required: false,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'stubLink',
        key: '{{stubLink}}',
        description: 'Optional link to edit show details',
        example: 'https://portal.littlebooker.com/shows/my-show?stub=abc123',
        required: false,
        dataType: 'url',
        category: 'happening',
      },
      {
        name: 'editLinkHtml',
        key: '{{editLinkHtml}}',
        description: 'HTML button/link to edit show details (auto-generated if stubLink provided)',
        example: '<a href="...">Edit Show Details →</a>',
        required: false,
        dataType: 'html',
        category: 'happening',
      },
      {
        name: 'editLinkText',
        key: '{{editLinkText}}',
        description: 'Plain text link to edit show details (auto-generated if stubLink provided)',
        example: 'Edit Show Details: https://...',
        required: false,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_HAPPENING_REMINDER_SUBJECT,
    defaultHtml: DEFAULT_HAPPENING_REMINDER_HTML,
    defaultText: DEFAULT_HAPPENING_REMINDER_TEXT,
    sampleData: {
      toEmail: 'eve@example.com',
      toName: 'Eve',
      happeningTitle: 'Weekend Spotlight Session',
      happeningStartTime: 'Saturday, January 27, 2026 at 6:00 PM',
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
      locationSectionHtml: `
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
          <div style="font-size: 14px;">
            <strong style="color: #6b7280;">Location:</strong>
            <div style="margin-top: 5px; color: #111827;">
              Main Studio<br>456 Performance Ave, Rotterdam
            </div>
          </div>
        </div>
      `,
      locationSectionText: 'Location: Main Studio\n456 Performance Ave, Rotterdam',
      arrivalSectionHtml: `
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
          <div style="font-size: 14px;">
            <strong style="color: #6b7280;">Arrival:</strong>
            <div style="margin-top: 5px; color: #111827;">
              Please arrive at 5:30 PM for sound check
            </div>
          </div>
        </div>
      `,
      arrivalSectionText: 'Arrival: Please arrive at 5:30 PM for sound check',
      arrivalInfo: 'Please arrive at 5:30 PM for sound check',
      happeningPlace: {
        name: 'Main Studio',
        address: '456 Performance Ave, Rotterdam',
      },
      stubLink: 'https://portal.littlebooker.com/shows/weekend-spotlight?stub=abc123',
      editLinkHtml: `
        <div style="text-align: center; margin: 25px 0;">
          <a href="https://portal.littlebooker.com/shows/weekend-spotlight?stub=abc123"
             style="display: inline-block; background: #667eea; color: white; text-decoration: none; padding: 12px 30px; border-radius: 6px; font-weight: 600; font-size: 16px;">
            Edit Show Details →
          </a>
        </div>
      `,
      editLinkText: 'Edit Show Details: https://portal.littlebooker.com/shows/weekend-spotlight?stub=abc123',
    },
  },

  completionNotification: {
    type: 'completionNotification',
    name: 'Completion Notification',
    description: 'Sent to staff when an artist completes their profile or show details',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Recipient email address',
        example: 'programmer@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Recipient name',
        example: 'John Programmer',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'entityType',
        key: '{{entityType}}',
        description: 'Type of entity completed (person or happening)',
        example: 'person',
        required: true,
        dataType: 'string',
        category: 'entity',
      },
      {
        name: 'entityTypeLabel',
        key: '{{entityTypeLabel}}',
        description: 'Human-readable type label (Profile or Show Details)',
        example: 'Profile',
        required: true,
        dataType: 'string',
        category: 'entity',
      },
      {
        name: 'entityName',
        key: '{{entityName}}',
        description: 'Name of the entity (artist name or show title)',
        example: 'DJ Cosmic',
        required: true,
        dataType: 'string',
        category: 'entity',
      },
      {
        name: 'completedByName',
        key: '{{completedByName}}',
        description: 'Name of the person who completed the entity',
        example: 'DJ Cosmic',
        required: true,
        dataType: 'string',
        category: 'entity',
      },
      {
        name: 'completedAt',
        key: '{{completedAt}}',
        description: 'Formatted completion timestamp',
        example: 'Saturday, January 18, 2026 at 3:45 PM',
        required: true,
        dataType: 'date',
        category: 'entity',
      },
      {
        name: 'viewLink',
        key: '{{viewLink}}',
        description: 'Link to view the entity in the admin interface',
        example: 'https://admin.littlebooker.com/people?openEntityId=123',
        required: true,
        dataType: 'url',
        category: 'entity',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_COMPLETION_NOTIFICATION_SUBJECT,
    defaultHtml: DEFAULT_COMPLETION_NOTIFICATION_HTML,
    defaultText: DEFAULT_COMPLETION_NOTIFICATION_TEXT,
    sampleData: {
      toEmail: 'programmer@example.com',
      toName: 'John',
      entityType: 'person',
      entityTypeLabel: 'Profile',
      entityName: 'DJ Cosmic',
      completedByName: 'DJ Cosmic',
      completedAt: 'Saturday, January 18, 2026 at 3:45 PM',
      viewLink: 'https://admin.littlebooker.com/people?openEntityId=123&entityType=person',
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
    },
  },

  artistAndShowInvite: {
    type: 'artistAndShowInvite',
    name: 'Combined Artist & Show Invite',
    description: 'Show-first invitation: artist needs to complete profile and add show details',
    variables: [
      {
        name: 'artistName',
        key: '{{artistName}}',
        description: 'Artist name',
        example: 'DJ Nova',
        required: true,
        dataType: 'string',
        category: 'artist',
      },
      {
        name: 'missingProfileFields',
        key: '{{missingProfileFields}}',
        description: 'Comma-separated list of missing profile fields',
        example: 'bio, photo, phone number',
        required: true,
        dataType: 'string',
        category: 'artist',
      },
      {
        name: 'profileLink',
        key: '{{profileLink}}',
        description: 'Link to artist profile completion page',
        example: 'https://portal.littlebooker.com/people/dj-nova?invite=abc123',
        required: true,
        dataType: 'url',
        category: 'artist',
      },
      {
        name: 'happeningTitle',
        key: '{{happeningTitle}}',
        description: 'Show title',
        example: 'Late Night Sessions',
        required: true,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'happeningStartTime',
        key: '{{happeningStartTime}}',
        description: 'Show start date/time',
        example: 'Friday, Jan 26 at 10:00 PM',
        required: true,
        dataType: 'date',
        category: 'happening',
      },
      {
        name: 'happeningPlace',
        key: '{{happeningPlace}}',
        description: 'Venue name',
        example: 'The Warehouse',
        required: true,
        dataType: 'string',
        category: 'happening',
      },
      {
        name: 'showLink',
        key: '{{showLink}}',
        description: 'Link to show details form',
        example: 'https://portal.littlebooker.com/shows/late-night-sessions?stub=xyz789',
        required: true,
        dataType: 'url',
        category: 'happening',
      },
      {
        name: 'combinedLink',
        key: '{{combinedLink}}',
        description: 'Single link to combined profile + show details page',
        example: 'https://portal.littlebooker.com/invite/dj-nova/late-night-sessions?invite=abc123&stub=xyz789',
        required: false,
        dataType: 'url',
        category: 'happening',
      },
      {
        name: 'happeningDuration',
        key: '{{happeningDuration}}',
        description: 'Show duration in minutes',
        example: '120',
        required: true,
        dataType: 'number',
        category: 'happening',
      },
      {
        name: 'completionDeadline',
        key: '{{completionDeadline}}',
        description: 'Formatted deadline for completing profile and show details',
        example: 'Wednesday, January 24, 2026',
        required: false,
        dataType: 'date',
        category: 'happening',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Organization name',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#3b82f6',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#8b5cf6',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_ARTIST_AND_SHOW_INVITE_SUBJECT,
    defaultHtml: DEFAULT_ARTIST_AND_SHOW_INVITE_HTML,
    defaultText: DEFAULT_ARTIST_AND_SHOW_INVITE_TEXT,
    sampleData: {
      artistName: 'DJ Nova',
      missingProfileFields: 'bio, photo, phone number',
      profileLink: 'https://portal.littlebooker.com/people/dj-nova?invite=abc123',
      happeningTitle: 'Late Night Sessions',
      happeningStartTime: 'Friday, Jan 26 at 10:00 PM',
      happeningPlace: 'The Warehouse',
      showLink: 'https://portal.littlebooker.com/shows/late-night-sessions?stub=xyz789',
      combinedLink: 'https://portal.littlebooker.com/invite/dj-nova/late-night-sessions?invite=abc123&stub=xyz789',
      happeningDuration: 120,
      completionDeadline: 'Wednesday, January 24, 2026',
      organizationName: 'Radio Paradise',
      primaryColor: '#3b82f6',
      secondaryColor: '#8b5cf6',
      logoHtml: '',
    },
  },

  residencyCreation: {
    type: 'residencyCreation',
    name: 'Residency Creation',
    description: 'Sent to default artists when a new residency (series) is created with scheduled shows',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Recipient email address',
        example: 'artist@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Recipient name',
        example: 'DJ Cosmic',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'seriesName',
        key: '{{seriesName}}',
        description: 'Name of the residency/series',
        example: 'Friday Night Sessions',
        required: true,
        dataType: 'string',
        category: 'series',
      },
      {
        name: 'seriesDescription',
        key: '{{seriesDescription}}',
        description: 'Description of the series',
        example: 'Weekly electronic music showcase',
        required: false,
        dataType: 'string',
        category: 'series',
      },
      {
        name: 'episodeCount',
        key: '{{episodeCount}}',
        description: 'Total number of episodes/shows created',
        example: '12',
        required: true,
        dataType: 'number',
        category: 'series',
      },
      {
        name: 'frequency',
        key: '{{frequency}}',
        description: 'Human-readable recurrence pattern',
        example: 'Weekly on Fridays',
        required: true,
        dataType: 'string',
        category: 'series',
      },
      {
        name: 'showListHtml',
        key: '{{showListHtml}}',
        description: 'HTML formatted list of shows with dates and edit links',
        example: '<div>...</div>',
        required: true,
        dataType: 'html',
        category: 'series',
      },
      {
        name: 'showListText',
        key: '{{showListText}}',
        description: 'Plain text list of shows with dates and edit links',
        example: '1. Friday, Jan 26 - https://...',
        required: true,
        dataType: 'string',
        category: 'series',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_RESIDENCY_CREATION_SUBJECT,
    defaultHtml: DEFAULT_RESIDENCY_CREATION_HTML,
    defaultText: DEFAULT_RESIDENCY_CREATION_TEXT,
    sampleData: {
      toEmail: 'artist@example.com',
      toName: 'DJ Cosmic',
      seriesName: 'Friday Night Sessions',
      seriesDescription: 'Weekly electronic music showcase',
      episodeCount: 12,
      frequency: 'Weekly on Fridays',
      showListHtml: `
        <div style="border: 1px solid #e5e7eb; border-radius: 4px; overflow: hidden;">
          <div style="display: flex; justify-content: space-between; padding: 12px 15px; border-bottom: 1px solid #e5e7eb; background: #f9fafb;">
            <span style="font-weight: 600; color: #111827;">Ep. 1 - Friday, Jan 26, 2026 at 10:00 PM</span>
            <a href="https://portal.example.com/shows/friday-night-sessions-ep1?stub=abc123" style="color: #667eea; text-decoration: none;">Edit Details →</a>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 12px 15px; border-bottom: 1px solid #e5e7eb;">
            <span style="font-weight: 600; color: #111827;">Ep. 2 - Friday, Feb 2, 2026 at 10:00 PM</span>
            <a href="https://portal.example.com/shows/friday-night-sessions-ep2?stub=def456" style="color: #667eea; text-decoration: none;">Edit Details →</a>
          </div>
        </div>
      `,
      showListText: `1. Ep. 1 - Friday, Jan 26, 2026 at 10:00 PM
   Edit: https://portal.example.com/shows/friday-night-sessions-ep1?stub=abc123

2. Ep. 2 - Friday, Feb 2, 2026 at 10:00 PM
   Edit: https://portal.example.com/shows/friday-night-sessions-ep2?stub=def456`,
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
    },
  },

  postShow: {
    type: 'postShow',
    name: 'Post-Show Follow-up',
    description: 'Sent to artists after a show ends with a tracklist publish link and optional feedback form',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Artist email address',
        example: 'artist@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Artist name',
        example: 'DJ Cosmic',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'happeningTitle',
        key: '{{happeningTitle}}',
        description: 'Title of the show',
        example: 'Friday Night Sessions Ep. 42',
        required: true,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'happeningStartTime',
        key: '{{happeningStartTime}}',
        description: 'Formatted start time of the show',
        example: 'Friday, January 26, 2026 at 10:00 PM [CET]',
        required: true,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'tracklistLink',
        key: '{{tracklistLink}}',
        description: 'Link to the show stub page tracklist section',
        example: 'https://portal.example.com/shows/friday-night-ep42?stub=abc123#tracklist',
        required: true,
        dataType: 'url',
        category: 'show',
      },
      {
        name: 'feedbackSectionHtml',
        key: '{{feedbackSectionHtml}}',
        description: 'HTML feedback form section (empty if no feedback URL configured)',
        example: '<div style="text-align: center; margin: 15px 0;"><a href="https://forms.example.com/feedback" style="display: inline-block; background: #667eea; color: white; text-decoration: none; padding: 12px 30px; border-radius: 6px; font-weight: 600; font-size: 15px;">Share Your Feedback →</a></div>',
        required: false,
        dataType: 'html',
        category: 'show',
      },
      {
        name: 'feedbackSectionText',
        key: '{{feedbackSectionText}}',
        description: 'Plain text feedback form section (empty if no feedback URL configured)',
        example: 'SHARE YOUR FEEDBACK:\nhttps://forms.example.com/feedback',
        required: false,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_POST_SHOW_SUBJECT,
    defaultHtml: DEFAULT_POST_SHOW_HTML,
    defaultText: DEFAULT_POST_SHOW_TEXT,
    sampleData: {
      toEmail: 'artist@example.com',
      toName: 'DJ Cosmic',
      happeningTitle: 'Friday Night Sessions Ep. 42',
      happeningStartTime: 'Friday, January 26, 2026 at 10:00 PM [CET]',
      tracklistLink: 'https://portal.example.com/shows/friday-night-ep42?stub=abc123#tracklist',
      feedbackSectionHtml: `
        <div style="text-align: center; margin: 15px 0;">
          <a href="https://forms.example.com/feedback"
             style="display: inline-block; background: #667eea; color: white; text-decoration: none; padding: 12px 30px; border-radius: 6px; font-weight: 600; font-size: 15px;">
            Share Your Feedback →
          </a>
        </div>
      `,
      feedbackSectionText: `SHARE YOUR FEEDBACK:\nhttps://forms.example.com/feedback`,
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
    },
  },

  technicalUpdateNotification: {
    type: 'technicalUpdateNotification',
    name: 'Technical Update Notification',
    description: 'Sent to engineers when an artist updates equipment requests or technical notes',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Recipient email address',
        example: 'engineer@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Recipient name',
        example: 'Jane Engineer',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'happeningTitle',
        key: '{{happeningTitle}}',
        description: 'Title of the show',
        example: 'Friday Night Sessions Ep. 42',
        required: true,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'updatedByName',
        key: '{{updatedByName}}',
        description: 'Name of the artist who made the update',
        example: 'DJ Cosmic',
        required: true,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'updatedAt',
        key: '{{updatedAt}}',
        description: 'Formatted update timestamp',
        example: 'Saturday, January 18, 2026 at 3:45 PM',
        required: true,
        dataType: 'date',
        category: 'show',
      },
      {
        name: 'changeDescription',
        key: '{{changeDescription}}',
        description: 'Description of what changed (equipment requests, technical notes, or both)',
        example: 'Equipment requests and technical notes updated',
        required: true,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'viewLink',
        key: '{{viewLink}}',
        description: 'Link to view the show in the admin interface',
        example: 'https://admin.littlebooker.com/happenings?openEntityId=123',
        required: true,
        dataType: 'url',
        category: 'show',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_TECHNICAL_UPDATE_NOTIFICATION_SUBJECT,
    defaultHtml: DEFAULT_TECHNICAL_UPDATE_NOTIFICATION_HTML,
    defaultText: DEFAULT_TECHNICAL_UPDATE_NOTIFICATION_TEXT,
    sampleData: {
      toEmail: 'engineer@example.com',
      toName: 'Jane Engineer',
      happeningTitle: 'Friday Night Sessions Ep. 42',
      updatedByName: 'DJ Cosmic',
      updatedAt: 'Saturday, January 18, 2026 at 3:45 PM',
      changeDescription: 'Equipment requests and technical notes updated',
      viewLink: 'https://admin.littlebooker.com/happenings?openEntityId=123',
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
    },
  },

  archiveNotification: {
    type: 'archiveNotification',
    name: 'Archive Notification',
    description: 'Sent to artists when an archive/embed link is added to their show, with a link to the show page and the hosting service(s)',
    variables: [
      {
        name: 'toEmail',
        key: '{{toEmail}}',
        description: 'Artist email address',
        example: 'artist@example.com',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'toName',
        key: '{{toName}}',
        description: 'Artist name',
        example: 'DJ Cosmic',
        required: true,
        dataType: 'string',
        category: 'recipient',
      },
      {
        name: 'happeningTitle',
        key: '{{happeningTitle}}',
        description: 'Title of the show',
        example: 'Friday Night Sessions Ep. 42',
        required: true,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'happeningStartTime',
        key: '{{happeningStartTime}}',
        description: 'Formatted start time of the show',
        example: 'Friday, January 26, 2026 at 10:00 PM [CET]',
        required: true,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'showPageSectionHtml',
        key: '{{showPageSectionHtml}}',
        description: 'HTML block linking to the public show page (empty if no website base URL configured)',
        example: '<div style="text-align: center; margin: 15px 0;"><a href="https://radio.example.com/shows/friday-night-ep42">View show page</a></div>',
        required: false,
        dataType: 'html',
        category: 'show',
      },
      {
        name: 'showPageSectionText',
        key: '{{showPageSectionText}}',
        description: 'Plain text block linking to the public show page (empty if no website base URL configured)',
        example: 'Show page: https://radio.example.com/shows/friday-night-ep42\n',
        required: false,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'archiveLinksHeading',
        key: '{{archiveLinksHeading}}',
        description: 'Heading shown above the list of archive links. Changes based on whether a public show page link is included.',
        example: 'Besides the website, the show is also archived on:',
        required: true,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'archiveLinksHtml',
        key: '{{archiveLinksHtml}}',
        description: 'HTML list of archive links (SoundCloud, custom hosts, etc)',
        example: '<a href="https://soundcloud.com/.../">SoundCloud</a>',
        required: true,
        dataType: 'html',
        category: 'show',
      },
      {
        name: 'archiveLinksText',
        key: '{{archiveLinksText}}',
        description: 'Plain text list of archive links',
        example: 'SoundCloud: https://soundcloud.com/.../',
        required: true,
        dataType: 'string',
        category: 'show',
      },
      {
        name: 'organizationName',
        key: '{{organizationName}}',
        description: 'Name of the organization',
        example: 'Radio Paradise',
        required: false,
        dataType: 'string',
        category: 'organization',
      },
      {
        name: 'primaryColor',
        key: '{{primaryColor}}',
        description: 'Primary brand color',
        example: '#667eea',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'secondaryColor',
        key: '{{secondaryColor}}',
        description: 'Secondary brand color',
        example: '#764ba2',
        required: false,
        dataType: 'string',
        category: 'branding',
      },
      {
        name: 'logoHtml',
        key: '{{logoHtml}}',
        description: 'HTML for organization logo',
        example: '<img src="https://example.com/logo.png" alt="Logo" />',
        required: false,
        dataType: 'html',
        category: 'branding',
      },
    ],
    defaultSubject: DEFAULT_ARCHIVE_NOTIFICATION_SUBJECT,
    defaultHtml: DEFAULT_ARCHIVE_NOTIFICATION_HTML,
    defaultText: DEFAULT_ARCHIVE_NOTIFICATION_TEXT,
    sampleData: {
      toEmail: 'artist@example.com',
      toName: 'DJ Cosmic',
      happeningTitle: 'Friday Night Sessions Ep. 42',
      happeningStartTime: 'Friday, January 26, 2026 at 10:00 PM [CET]',
      showPageSectionHtml: `
        <div style="text-align: center; margin: 15px 0;">
          <a href="https://radio.example.com/shows/friday-night-ep42"
             style="display: inline-block; background: #667eea; color: white; text-decoration: none; padding: 12px 30px; border-radius: 6px; font-weight: 600; font-size: 15px;">
            View show page →
          </a>
        </div>
      `,
      showPageSectionText: 'Show page: https://radio.example.com/shows/friday-night-ep42\n',
      archiveLinksHeading: 'Besides the website, the show is also archived on:',
      archiveLinksHtml: '<ul style="margin:0;padding-left:20px;"><li><a href="https://soundcloud.com/example/show">SoundCloud</a></li></ul>',
      archiveLinksText: '- SoundCloud: https://soundcloud.com/example/show',
      organizationName: 'Radio Paradise',
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoHtml: '',
    },
  },
};

/**
 * Get email template schema by type
 */
export function getEmailTemplateSchema(type: EmailType): EmailTemplateSchema | undefined {
  return EMAIL_TEMPLATE_REGISTRY[type];
}

/**
 * Get all email template types
 */
export function getAllEmailTemplateTypes(): EmailType[] {
  return Object.keys(EMAIL_TEMPLATE_REGISTRY) as EmailType[];
}

/**
 * Get all template schemas
 */
export function getAllEmailTemplateSchemas(): EmailTemplateSchema[] {
  return Object.values(EMAIL_TEMPLATE_REGISTRY);
}
